/**
 * /studio — the Studio's front door: your projects, and the step that creates one.
 *
 * WHAT CHANGED AND WHY. This route used to be a RESOLVER: it picked a song (the one you
 * were last on, else the newest) and immediately `<Navigate>`d to the workspace at
 * /library/:songId. Batch 6 §3 asks for the opposite — "when a user clicks the Studio
 * navigation item, do not immediately throw them into an empty workspace" — so the Studio
 * now opens on a screen where you decide what you are working on before the workspace
 * loads. Name a project, choose ONE library song or ALL of them, and open it.
 *
 * WHAT WAS KEPT. The resolver's answer to "which song" is not thrown away, it is moved
 * behind an explicit gesture: `openProject` below picks the SAME song by the SAME rule
 * (the track you were last working on, else the newest by `seq`), so choosing a project
 * lands where the redirect used to land — the difference is that a person asked for it.
 * And nothing about the workspace itself is touched: it is still /library/:songId, still
 * one song at a time, and the Library still opens it directly from a song row. This screen
 * is a step in front of the Studio, not a second Studio.
 *
 * NOTHING HERE IS A SECOND MUSIC STORE, which §3 rules out by name. A project holds song
 * IDS and every surface resolves them against songsStore at render time — see the
 * `StudioProject` type. The library the picker offers is the library.
 *
 * PERSISTENCE IS NOT PRETENDED, which §3 also asks for explicitly. Projects live in memory
 * for the session because studioProjectsApi is a mock, and the note under the list says so
 * on screen rather than only in a comment. See api/studioProjectsApi.ts for the endpoints.
 *
 * THE UPLOAD PANEL IS UNAFFECTED. Studio audio upload is a tool inside the workspace
 * (drawer === 'upload' in routes/Studio.tsx → components/studio/UploadPanel.tsx). This
 * route does not touch it, and the way to it is unchanged: open a project, then the Upload
 * tool in the rail.
 */
import { AlertCircle, Disc3, FolderOpen, Music2, RefreshCw } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Btn, LoadError, LoadSkeleton } from '../components/ui';
import { NewProjectForm, ProjectCard, StudioHeader } from '../components/studio';
import { UNKNOWN_API_ERROR_MESSAGE } from '../api/errors';
import { useSongs, useStudioProjects } from '../hooks';
import { isFirstLoad } from '../stores/loadState';
import { useSongsStore } from '../stores/songsStore';
import { useStudioProjectsStore } from '../stores/studioProjectsStore';
import { useStudioStore } from '../stores/studioStore';
import { c } from '../styles/theme';
import type { StudioProject } from '../types/domain';
import { paths } from './paths';

export default function StudioEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { songs, getSong } = useSongs();
  const {
    projects,
    activeProject,
    songsIn,
    summaryFor,
    nameError,
    createProject,
    deleteProject,
    renameProject,
    setActiveProject,
  } = useStudioProjects();
  // Read, never written here: this screen decides where to go, and the workspace is what
  // records having been there.
  const lastSongId = useStudioStore((s) => s.lastSongId);
  const resumeStudio = useStudioStore((s) => s.resumeStudio);
  // The projects read, from its own store — the same pair every collection screen reads.
  const load = useStudioProjectsStore((s) => s.load);
  const loadProjects = useStudioProjectsStore((s) => s.loadProjects);
  /*
    THE SECOND READ ON THIS SCREEN, and the one it was not consulting. Two independent
    collections are rendered here — projects from studioProjectsStore, and the LIBRARY, which
    the form's song picker and the note below are both about — and only the projects one had
    its states separated. `songs.length === 0` was read as "you have no songs" during the
    library fetch and permanently after a failed one, on the same screen that already
    distinguishes the three states correctly for projects a few lines down.

    Only the failure needs its own wording here. There is no skeleton branch, deliberately:
    the library is not this screen's subject — it is an input to one form field and one note —
    and blanking the create form behind a spinner to wait for a list it can work without
    would be worse than the note it replaces. The wording during a first read is the same as
    for a genuinely empty library, and both are honest: nothing is on offer yet.
  */
  const songsLoadStatus = useSongsStore((s) => s.load.status);
  const loadSongs = useSongsStore((s) => s.loadSongs);
  const libraryUnavailable = songsLoadStatus === 'error' && songs.length === 0;

  /**
   * Which of a project's songs the workspace should open on.
   *
   * THE RESOLVER'S OLD RULE, unchanged and now scoped to the project: the track you were
   * last working on if it is in this project, else the project's newest by `seq`.
   *
   * `getSong` is what validates the remembered id — it can name a song that has since been
   * deleted, and following it would land the workspace on a route whose own guard bounces
   * straight back to the Library.
   *
   * "Newest" is `seq`, the same monotonic counter the Library sorts by, rather than the
   * first entry: `songIds` is the order the songs were ADDED to the project, which is not
   * the order they were made.
   */
  function targetSong(project: StudioProject) {
    const tracks = songsIn(project);
    if (tracks.length === 0) return null;
    const resumed = getSong(lastSongId ?? undefined);
    if (resumed && project.songIds.includes(resumed.id)) return resumed;
    return tracks.reduce((best, song) => ((song.seq ?? 0) > (best.seq ?? 0) ? song : best), tracks[0]);
  }

  /**
   * Opens a project in the workspace.
   *
   * Marks it active BEFORE navigating, so the workspace's library strip is scoped to this
   * project on its first render rather than showing the whole library for one frame.
   *
   * A project with no resolvable tracks does not navigate: the workspace works on a song
   * and there is none. The card's button is disabled in that state, so this is the same
   * rule enforced at the funnel rather than only in the UI.
   */
  function openProject(project: StudioProject) {
    const target = targetSong(project);
    if (!target) return;
    setActiveProject(project.id);
    navigate(paths.song(target.id));
  }

  const showProjects = searchParams.get('projects') === '1';
  // Returning to Studio should reopen the last workspace even when the user entered it
  // directly from Library rather than through a project. The explicit projects URL keeps
  // its picker behavior, while `/studio` resumes the last song when one exists.
  const resumeTarget = !showProjects
    ? activeProject
      ? targetSong(activeProject)
      : resumeStudio
        ? getSong(lastSongId ?? undefined)
        : null
    : null;
  const resumedRef = useRef(false);
  const initialActiveProjectId = useRef(showProjects ? null : activeProject?.id ?? null);

  useEffect(() => {
    if (!resumeTarget || resumedRef.current) return;
    if (activeProject && activeProject.id !== initialActiveProjectId.current) {
      return;
    }
    resumedRef.current = true;
    navigate(paths.song(resumeTarget.id), { replace: true });
  }, [activeProject, navigate, resumeTarget]);

  if (resumeTarget && (!activeProject || activeProject.id === initialActiveProjectId.current)) return null;

  return (
    <div>
      <StudioHeader title="Lyrixsa Studio" subtitle="Start a project, then open the workspace on its tracks." />

      <NewProjectForm
        songs={songs}
        nameError={nameError}
        /*
          CREATED, NOT OPENED. Creating and entering are two decisions, and a form that
          navigated away on submit would give no chance to see that the project exists or
          to make a second one — it would also be the "thrown straight into the workspace"
          behaviour this screen exists to remove, one step later. The new project appears
          at the top of the list below with its own explicit "Open in Studio".

          The return value is ignored here rather than checked: the hook returns null only
          for a name the form has already refused, and the toast it raises on success is
          the confirmation. Nothing on this screen depends on the record.
        */
        onCreate={createProject}
        // Which of the two reasons the picker has nothing to offer. See the prop's doc.
        libraryUnavailable={libraryUnavailable}
      />

      {songs.length === 0 && (
        /*
          Nothing to work on, said once and quietly rather than as the whole screen.

          The form above still works either way — a project made before any songs exist is a
          name waiting for work, which the `StudioProject` type documents as a real state — so
          this is a note with the way forward, not a refusal. Which note depends on WHY the
          library is empty; see `libraryUnavailable`.
        */
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            border: `1px dashed ${c.border}`,
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 18,
          }}
        >
          {libraryUnavailable ? (
            <>
              <AlertCircle size={16} color={c.text3} aria-hidden="true" />
              {/* A live region, because this replaces a note that was already on screen
                  saying the opposite about the user's own data. */}
              <p
                role="alert"
                style={{ fontSize: 12, color: c.text2, margin: 0, flex: 1, minWidth: 180 }}
              >
                Your library could not be loaded, so there are no tracks to choose from. This is
                not a statement that you have no songs — nothing has happened to them.
              </p>
              {/* Retry rather than "Create music": sending someone to write a new song
                  because a request failed is the wrong instruction, and it is the one the
                  single note used to give. */}
              <Btn onClick={() => void loadSongs()} style={{ height: 32 }}>
                <RefreshCw size={13} />
                Try again
              </Btn>
            </>
          ) : (
            <>
              <Disc3 size={16} color={c.text3} aria-hidden="true" />
              <p style={{ fontSize: 12, color: c.text2, margin: 0, flex: 1, minWidth: 180 }}>
                You have no songs yet. A project can wait for them, but the workspace opens on a
                track — so create some music first.
              </p>
              <Btn onClick={() => navigate(paths.create)} style={{ height: 32 }}>
                <Music2 size={13} />
                Create music
              </Btn>
            </>
          )}
        </div>
      )}

      <p className="lyx-ws-eyebrow" style={{ marginBottom: 8 }}>
        Your projects
      </p>

      {/* The four read states, as on the Library and the Playlists. `projects.length === 0`
          alone said "No projects yet" for a fetch that had not returned and for one that
          failed — and on this screen that reads as lost work, because the note underneath the
          list already warns that projects are not saved to an account. An outage must not
          look like the warning coming true. */}
      {isFirstLoad(load, projects.length > 0) ? (
        <LoadSkeleton rows={3} label="Loading your projects…" />
      ) : load.status === 'error' && projects.length === 0 ? (
        <LoadError message={load.error?.message ?? UNKNOWN_API_ERROR_MESSAGE} onRetry={loadProjects}>
          <p style={{ fontSize: 12, color: c.text2, margin: '8px 0 0' }}>
            You can still name a project above — it just cannot be listed with the others until
            this read succeeds.
          </p>
        </LoadError>
      ) : projects.length === 0 ? (
        // The Library's and the Playlists' empty state, in the same shape: small icon, one
        // line, and the action — which here is the form above, so this only explains what
        // for.
        <div style={{ padding: '24px 0 12px', textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              margin: '0 auto 14px',
              borderRadius: 10,
              background: c.surface2,
              border: `1px solid ${c.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FolderOpen size={17} color={c.text3} />
          </div>
          <p
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 14,
              margin: '0 0 4px',
            }}
          >
            No projects yet
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
            Name one above and pick its tracks. Opening a project opens the Studio workspace.
          </p>
        </div>
      ) : (
        <>
          <div className="lyx-song-grid">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                songs={songsIn(project)}
                summary={summaryFor(project)}
                current={activeProject?.id === project.id}
                onOpen={openProject}
                onRename={(p) => {
                  /*
                    window.prompt, which is what the Studio's "Rename version" already
                    uses (routes/Studio.tsx). Not a new dialog: renaming is one short
                    string and the app has no rename-project modal, so borrowing the
                    pattern that is already here beats inventing a second one for a
                    feature this small. The hook refuses an empty or unchanged name.
                  */
                  const next = window.prompt('Rename project', p.name);
                  if (next !== null) renameProject(p, next);
                }}
                onDelete={deleteProject}
              />
            ))}
          </div>

          {/*
            SAID ON SCREEN, not just in a comment. §3 asks that persistence not be
            pretended, and a list of named work is exactly the place a user would assume
            it. See api/studioProjectsApi.ts.

            text2, NOT text3, and the distinction matters more here than anywhere else on
            the screen: this is the one sentence telling a user their work will be gone.
            A caveat faded below readable contrast is a caveat that was not really given.
          */}
        </>
      )}
    </div>
  );
}
